import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form", 
    "clientId", 
    "clientNameInput",
    "quantity",
    "widthMm", 
    "heightMm", 
    "depthMm", 
    "reference", 
    "description", 
    "color", 
    "exteriorColor", 
    "interiorColor", 
    "notes",
    "lineItemId",
    "submitButton",
    "modalTitle"
  ]

  connect() {
    this.resetForm()
    
    // Listen for modal events
    const modal = document.getElementById("line-item-modal")
    if (modal) {
      modal.addEventListener("modal:opened", this.handleModalOpened.bind(this))
      modal.addEventListener("modal:closed", this.handleModalClosed.bind(this))
    }

    // Add form validation on submit
    this.formTarget.addEventListener('submit', this.validateForm.bind(this))
    
    // Add real-time validation on input changes
    this.addInputValidationListeners()
  }

  disconnect() {
    // Clean up event listeners
    const modal = document.getElementById("line-item-modal")
    if (modal) {
      modal.removeEventListener("modal:opened", this.handleModalOpened.bind(this))
      modal.removeEventListener("modal:closed", this.handleModalClosed.bind(this))
    }
    
    this.formTarget.removeEventListener('submit', this.validateForm.bind(this))
  }

  addInputValidationListeners() {
    // Add blur event listeners for real-time validation
    const fields = [
      'quantity', 'widthMm', 'heightMm', 'depthMm', 
      'reference', 'description', 'color', 'exteriorColor', 'interiorColor'
    ]
    
    fields.forEach(field => {
      if (this.hasTarget(field)) {
        this[`${field}Target`].addEventListener('blur', () => this.validateField(field))
        this[`${field}Target`].addEventListener('input', () => this.clearFieldError(field))
      }
    })
  }

  validateForm(event) {
    // Clear any existing errors first
    this.clearAllErrors()
    
    const orderType = this.getOrderType()
    let isValid = true
    
    // Validate quantity
    if (!this.validateQuantity()) {
      isValid = false
    }
    
    // Validate type-specific fields
    if (orderType === 'solar_panel') {
      if (!this.validateSolarPanelFields()) {
        isValid = false
      }
    } else if (orderType === 'glass') {
      if (!this.validateGlassFields()) {
        isValid = false
      }
    } else if (orderType === 'blind') {
      if (!this.validateBlindFields()) {
        isValid = false
      }
    } else if (orderType === 'aluminium') {
      if (!this.validateAluminiumFields()) {
        isValid = false
      }
    } else if (orderType === 'other') {
      if (!this.validateOtherFields()) {
        isValid = false
      }
    }
    
    // If validation fails, prevent form submission
    if (!isValid) {
      event.preventDefault()
      event.stopPropagation()
      this.showValidationSummary()
      return false
    }
    
    return true
  }

  validateQuantity() {
    const quantity = this.quantityTarget.value.trim()
    
    if (!quantity || quantity === '' || quantity === '0' || parseInt(quantity) <= 0) {
      this.showFieldError('quantity', 'Quantidade é obrigatória e deve ser maior que 0')
      return false
    }
    
    return true
  }

  validateSolarPanelFields() {
    let isValid = true
    
    // Reference is required
    if (!this.hasReferenceTarget || !this.referenceTarget.value.trim()) {
      this.showFieldError('reference', 'Referência é obrigatória para painéis solares')
      isValid = false
    }
    
    // Exterior color is required
    if (!this.hasExteriorColorTarget || !this.exteriorColorTarget.value.trim()) {
      this.showFieldError('exteriorColor', 'Cor exterior é obrigatória para painéis solares')
      isValid = false
    }
    
    // Interior color is required
    if (!this.hasInteriorColorTarget || !this.interiorColorTarget.value.trim()) {
      this.showFieldError('interiorColor', 'Cor interior é obrigatória para painéis solares')
      isValid = false
    }
    
    // All dimensions are required (including depth)
    if (!this.validateDimensions(true)) {
      isValid = false
    }
    
    return isValid
  }

  validateGlassFields() {
    let isValid = true
    
    // Description is required
    if (!this.hasDescriptionTarget || !this.descriptionTarget.value.trim()) {
      this.showFieldError('description', 'Descrição é obrigatória para vidros')
      isValid = false
    }
    
    // Width and height are required (espessura não necessária)
    if (!this.validateDimensions(false)) {
      isValid = false
    }
    
    return isValid
  }

  validateBlindFields() {
    let isValid = true
    
    // Description is required
    if (!this.hasDescriptionTarget || !this.descriptionTarget.value.trim()) {
      this.showFieldError('description', 'Descrição é obrigatória para estores')
      isValid = false
    }
    
    // Color is required
    if (!this.hasColorTarget || !this.colorTarget.value.trim()) {
      this.showFieldError('color', 'Cor é obrigatória para estores')
      isValid = false
    }
    
    // Width and height are required (espessura não necessária)
    if (!this.validateDimensions(false)) {
      isValid = false
    }
    
    return isValid
  }

  validateAluminiumFields() {
    let isValid = true
    
    // Reference is required
    if (!this.hasReferenceTarget || !this.referenceTarget.value.trim()) {
      this.showFieldError('reference', 'Referência é obrigatória para alumínio')
      isValid = false
    }
    
    // Exterior color is required
    if (!this.hasExteriorColorTarget || !this.exteriorColorTarget.value.trim()) {
      this.showFieldError('exteriorColor', 'Cor exterior é obrigatória para alumínio')
      isValid = false
    }
    
    // Interior color is required
    if (!this.hasInteriorColorTarget || !this.interiorColorTarget.value.trim()) {
      this.showFieldError('interiorColor', 'Cor interior é obrigatória para alumínio')
      isValid = false
    }
    
    // NO dimensions are required for aluminium (this is the key difference from solar panels)
    
    return isValid
  }

  validateOtherFields() {
    let isValid = true
    
    // Description is required
    if (!this.hasDescriptionTarget || !this.descriptionTarget.value.trim()) {
      this.showFieldError('description', 'Descrição é obrigatória')
      isValid = false
    }
    
    // Dimensions are optional for other items (no validation needed)
    
    return isValid
  }

  validateDimensions(requireDepth = true) {
    let isValid = true
    
    // Width is required
    if (!this.hasWidthMmTarget || !this.widthMmTarget.value.trim() || parseInt(this.widthMmTarget.value) <= 0) {
      this.showFieldError('widthMm', 'Largura é obrigatória e deve ser maior que 0')
      isValid = false
    }
    
    // Height is required
    if (!this.hasHeightMmTarget || !this.heightMmTarget.value.trim() || parseInt(this.heightMmTarget.value) <= 0) {
      this.showFieldError('heightMm', 'Altura é obrigatória e deve ser maior que 0')
      isValid = false
    }
    
    // Depth (Espessura) validation
    if (requireDepth) {
      if (!this.hasDepthMmTarget || !this.depthMmTarget.value.trim() || parseInt(this.depthMmTarget.value) <= 0) {
        this.showFieldError('depthMm', 'Espessura é obrigatória e deve ser maior que 0')
        isValid = false
      }
    }
    
    return isValid
  }

  validateField(fieldName) {
    this.clearFieldError(fieldName)
    
    const orderType = this.getOrderType()
    
    switch (fieldName) {
      case 'quantity':
        this.validateQuantity()
        break
      case 'reference':
        if ((orderType === 'solar_panel' || orderType === 'aluminium') && (!this.hasReferenceTarget || !this.referenceTarget.value.trim())) {
          const itemTypeName = orderType === 'solar_panel' ? 'painéis solares' : 'alumínio'
          this.showFieldError('reference', `Referência é obrigatória para ${itemTypeName}`)
        }
        break
      case 'description':
        if (['glass', 'blind', 'other'].includes(orderType) && (!this.hasDescriptionTarget || !this.descriptionTarget.value.trim())) {
          this.showFieldError('description', 'Descrição é obrigatória')
        }
        break
      case 'color':
        if (orderType === 'blind' && (!this.hasColorTarget || !this.colorTarget.value.trim())) {
          this.showFieldError('color', 'Cor é obrigatória para estores')
        }
        break
      case 'exteriorColor':
        if ((orderType === 'solar_panel' || orderType === 'aluminium') && (!this.hasExteriorColorTarget || !this.exteriorColorTarget.value.trim())) {
          const itemTypeName = orderType === 'solar_panel' ? 'painéis solares' : 'alumínio'
          this.showFieldError('exteriorColor', `Cor exterior é obrigatória para ${itemTypeName}`)
        }
        break
      case 'interiorColor':
        if ((orderType === 'solar_panel' || orderType === 'aluminium') && (!this.hasInteriorColorTarget || !this.interiorColorTarget.value.trim())) {
          const itemTypeName = orderType === 'solar_panel' ? 'painéis solares' : 'alumínio'
          this.showFieldError('interiorColor', `Cor interior é obrigatória para ${itemTypeName}`)
        }
        break
      case 'widthMm':
        // Dimensions are not required for aluminium or other types
        if (orderType !== 'aluminium' && orderType !== 'other' && (!this.hasWidthMmTarget || !this.widthMmTarget.value.trim() || parseInt(this.widthMmTarget.value) <= 0)) {
          this.showFieldError('widthMm', 'Largura é obrigatória e deve ser maior que 0')
        }
        break
      case 'heightMm':
        // Dimensions are not required for aluminium or other types
        if (orderType !== 'aluminium' && orderType !== 'other' && (!this.hasHeightMmTarget || !this.heightMmTarget.value.trim() || parseInt(this.heightMmTarget.value) <= 0)) {
          this.showFieldError('heightMm', 'Altura é obrigatória e deve ser maior que 0')
        }
        break
      case 'depthMm':
        // Dimensions are not required for aluminium or other types
        if (orderType !== 'aluminium' && orderType !== 'other' && (!this.hasDepthMmTarget || !this.depthMmTarget.value.trim() || parseInt(this.depthMmTarget.value) <= 0)) {
          this.showFieldError('depthMm', 'Espessura é obrigatória e deve ser maior que 0')
        }
        break
    }
  }

  showFieldError(fieldName, message) {
    if (!this.hasTarget(fieldName)) return
    
    const field = this[`${fieldName}Target`]
    const wrapper = field.closest('.form-field-wrapper')
    
    if (wrapper) {
      // Add error styling to the field
      field.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      field.classList.remove('focus:border-indigo-500', 'focus:ring-indigo-500')
      
      // Remove any existing error message
      const existingError = wrapper.querySelector('.field-error-message')
      if (existingError) {
        existingError.remove()
      }
      
      // Add error message
      const errorDiv = document.createElement('div')
      errorDiv.className = 'field-error-message text-sm text-red-600 mt-1'
      errorDiv.textContent = message
      wrapper.appendChild(errorDiv)
    }
  }

  clearFieldError(fieldName) {
    if (!this.hasTarget(fieldName)) return
    
    const field = this[`${fieldName}Target`]
    const wrapper = field.closest('.form-field-wrapper')
    
    if (wrapper) {
      // Remove error styling
      field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      field.classList.add('focus:border-indigo-500', 'focus:ring-indigo-500')
      
      // Remove error message
      const errorMessage = wrapper.querySelector('.field-error-message')
      if (errorMessage) {
        errorMessage.remove()
      }
    }
  }

  clearAllErrors() {
    const fields = [
      'quantity', 'widthMm', 'heightMm', 'depthMm', 
      'reference', 'description', 'color', 'exteriorColor', 'interiorColor'
    ]
    
    fields.forEach(field => {
      this.clearFieldError(field)
    })
    
    // Remove validation summary if it exists
    const existingSummary = this.formTarget.querySelector('.validation-summary')
    if (existingSummary) {
      existingSummary.remove()
    }
  }

  showValidationSummary() {
    // Remove existing summary
    const existingSummary = this.formTarget.querySelector('.validation-summary')
    if (existingSummary) {
      existingSummary.remove()
    }
    
    // Create validation summary
    const summaryDiv = document.createElement('div')
    summaryDiv.className = 'validation-summary bg-red-50 border border-red-200 rounded-md p-4 mb-4'
    
    const summaryContent = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            Correção de campos obrigatórios
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p>Por favor, corrija os erros indicados nos campos abaixo antes de continuar.</p>
          </div>
        </div>
      </div>
    `
    
    summaryDiv.innerHTML = summaryContent
    
    // Insert at the beginning of the form
    this.formTarget.insertBefore(summaryDiv, this.formTarget.firstChild)
    
    // Scroll to top of form to show the summary
    summaryDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  getOrderType() {
    const hiddenInput = this.formTarget.querySelector('input[name="line_item[item_type]"]')
    return hiddenInput ? hiddenInput.value : null
  }

  hasTarget(targetName) {
    return this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]
  }

  resetForm() {
    // Reset form to initial state for adding new items
    // Note: form action and method are determined by the template and shouldn't be reset here
    
    // Remove any hidden fields that might have been added for editing
    this.formTarget.querySelectorAll('input[name="_method"], input[name="line_item_id"]').forEach(el => {
      el.remove()
    })
    
    // Update submit button text
    this.submitButtonTarget.textContent = "Adicionar Material"
    this.modalTitleTarget.textContent = "Adicionar Material"
    
    // Clear hidden line item ID
    if (this.hasLineItemIdTarget) {
      this.lineItemIdTarget.setAttribute('data-id', '')
    }

    // Reset form fields
    this.clientIdTarget.value = ""
    this.clientNameInputTarget.value = ""
    this.quantityTarget.value = "1"
    
    if (this.hasWidthMmTarget) this.widthMmTarget.value = ""
    if (this.hasHeightMmTarget) this.heightMmTarget.value = ""
    if (this.hasDepthMmTarget) this.depthMmTarget.value = ""
    if (this.hasReferenceTarget) this.referenceTarget.value = ""
    if (this.hasDescriptionTarget) this.descriptionTarget.value = ""
    if (this.hasColorTarget) this.colorTarget.value = ""
    if (this.hasExteriorColorTarget) this.exteriorColorTarget.value = ""
    if (this.hasInteriorColorTarget) this.interiorColorTarget.value = ""
    if (this.hasNotesTarget) this.notesTarget.value = ""
  }

  loadForEdit(event) {
    const lineItemId = event.currentTarget.dataset.lineItemId
    
    // Determine if we're in order_wizard or orders page
    const isWizard = window.location.pathname.includes('/order_wizard')
    
    // Get the order ID from the URL or data attribute
    let orderId = null
    if (!isWizard) {
      const urlMatch = window.location.pathname.match(/\/orders\/(\d+)/)
      if (urlMatch && urlMatch[1]) {
        orderId = urlMatch[1]
      }
    }
    
    // Determine the correct API endpoint
    let apiUrl
    if (isWizard) {
      apiUrl = `/order_wizard/edit_line_item?line_item_id=${lineItemId}`
    } else if (orderId) {
      apiUrl = `/orders/${orderId}/edit_line_item?line_item_id=${lineItemId}`
    } else {
      console.error("Cannot determine API endpoint for editing line item")
      return
    }
    
    // Fetch line item data
    fetch(apiUrl)
      .then(response => response.json())
      .then(data => {
        // Determine the form action URL based on whether we're in wizard or order page
        if (isWizard) {
          this.formTarget.action = "/order_wizard/update_line_item"
        } else if (orderId) {
          this.formTarget.action = `/orders/${orderId}/update_line_item`
        }
        
        // Set the form method
        this.formTarget.method = "post"
        
        // Add hidden method field for PATCH
        const methodInput = document.createElement("input")
        methodInput.type = "hidden"
        methodInput.name = "_method"
        methodInput.value = "patch"
        this.formTarget.appendChild(methodInput)
        
        // Add hidden line item ID as a direct parameter, not nested in line_item
        const lineItemIdInput = document.createElement("input")
        lineItemIdInput.type = "hidden"
        lineItemIdInput.name = "line_item_id"
        lineItemIdInput.value = data.id
        this.formTarget.appendChild(lineItemIdInput)
        
        // Also update the target for the controller
        if (this.hasLineItemIdTarget) {
          this.lineItemIdTarget.setAttribute('data-id', data.id)
        }
        
        // Update submit button text
        this.submitButtonTarget.textContent = "Atualizar Material"
        this.modalTitleTarget.textContent = "Editar Material"
        
        // Fill in form fields
        this.clientIdTarget.value = data.client_id || ""
        this.clientNameInputTarget.value = data.client && data.client.profile ? 
          (data.client.profile.company_name || `${data.client.profile.first_name} ${data.client.profile.last_name}`) : 
          ""
        this.quantityTarget.value = data.quantity
        
        if (this.hasWidthMmTarget && data.width_mm) this.widthMmTarget.value = data.width_mm
        if (this.hasHeightMmTarget && data.height_mm) this.heightMmTarget.value = data.height_mm
        if (this.hasDepthMmTarget && data.depth_mm) this.depthMmTarget.value = data.depth_mm
        if (this.hasReferenceTarget && data.reference) this.referenceTarget.value = data.reference
        if (this.hasDescriptionTarget && data.description) this.descriptionTarget.value = data.description
        if (this.hasColorTarget && data.color) this.colorTarget.value = data.color
        if (this.hasExteriorColorTarget && data.exterior_color) this.exteriorColorTarget.value = data.exterior_color
        if (this.hasInteriorColorTarget && data.interior_color) this.interiorColorTarget.value = data.interior_color
        if (this.hasNotesTarget && data.notes) this.notesTarget.value = data.notes
      })
      .catch(error => {
        console.error("Error fetching line item:", error)
      })
  }
  
  handleModalOpened() {
    // If we're not editing (no line item ID), reset the form
    if (!this.hasLineItemIdTarget || !this.lineItemIdTarget.getAttribute('data-id')) {
      this.resetForm()
    }
  }
  
  handleModalClosed() {
    // After the modal is closed, reset the form to ensure it's ready for
    // adding a new item next time
    this.resetForm()
  }

  loadForDuplicate(event) {
    // Begin by resetting the form so we start from a clean "new" state
    this.resetForm()

    const lineItemId = event.currentTarget.dataset.lineItemId

    // Determine if we're in the order wizard or regular order page
    const isWizard = window.location.pathname.includes('/order_wizard')

    // Extract order ID if we're on the order show page
    let orderId = null
    if (!isWizard) {
      const urlMatch = window.location.pathname.match(/\/orders\/(\d+)/)
      if (urlMatch && urlMatch[1]) {
        orderId = urlMatch[1]
      }
    }

    // Decide which endpoint to hit to fetch the existing line item JSON
    let apiUrl
    if (isWizard) {
      apiUrl = `/order_wizard/edit_line_item?line_item_id=${lineItemId}`
    } else if (orderId) {
      apiUrl = `/orders/${orderId}/edit_line_item?line_item_id=${lineItemId}`
    } else {
      console.error("Cannot determine API endpoint for duplicating line item")
      return
    }

    fetch(apiUrl)
      .then(response => response.json())
      .then(data => {
        // Update modal title to make the intention clear
        this.modalTitleTarget.textContent = "Duplicar Material"

        // IMPORTANT: Do *NOT* attach lineItemId nor _method override – we are creating a NEW record

        // Ensure the form submits via POST to the default add endpoint
        // (action attribute is already correct in the template, but if the page was previously in edit
        //  mode it might have been changed – so we reset it here)
        if (isWizard) {
          this.formTarget.action = "/order_wizard/add_line_item"
        } else if (orderId) {
          this.formTarget.action = `/orders/${orderId}/add_line_item`
        }
        this.formTarget.method = "post"

        // Populate the various input fields exactly like in loadForEdit but without hidden extras
        this.clientIdTarget.value = data.client_id || ""
        this.clientNameInputTarget.value = data.client && data.client.profile ?
          (data.client.profile.company_name || `${data.client.profile.first_name} ${data.client.profile.last_name}`) : ""
        this.quantityTarget.value = data.quantity

        if (this.hasWidthMmTarget && data.width_mm) this.widthMmTarget.value = data.width_mm
        if (this.hasHeightMmTarget && data.height_mm) this.heightMmTarget.value = data.height_mm
        if (this.hasDepthMmTarget && data.depth_mm) this.depthMmTarget.value = data.depth_mm
        if (this.hasReferenceTarget && data.reference) this.referenceTarget.value = data.reference
        if (this.hasDescriptionTarget && data.description) this.descriptionTarget.value = data.description
        if (this.hasColorTarget && data.color) this.colorTarget.value = data.color
        if (this.hasExteriorColorTarget && data.exterior_color) this.exteriorColorTarget.value = data.exterior_color
        if (this.hasInteriorColorTarget && data.interior_color) this.interiorColorTarget.value = data.interior_color
        if (this.hasNotesTarget && data.notes) this.notesTarget.value = data.notes
      })
      .catch(error => {
        console.error("Error fetching line item for duplication:", error)
      })
  }
} 
